import React, { useState, useEffect, useRef, useCallback, CSSProperties, FocusEvent, KeyboardEvent, ClipboardEvent } from 'react'
import clsx from 'clsx'
import { useFormikContext, ErrorMessage } from 'formik'
import shortId from '@/utils/short-id'
import { makeStyles } from '@mui/styles'
import { FillTransition } from '@/modules/base/transitions/index'
import useCopyToClipboard from '@/modules/_hooks/useCopyToClipboard'
import SVGIcon from '@/modules/base/components/svg-icon/svg-icon'
import IconInfo from '@assets/images/icons/info-icon.svg'
import lang from '@/lang'

import {
  useAiTextTransform,
} from '@ai-text-transform-bridge'

import copyClipboardIcon from '@assets/images/icons/copy-iban.svg'
import revealIcon from '@assets/images/icons/reveal.svg'
import CheckIcon from '@assets/images/icons/check.svg'
import CloseIcon from '@assets/images/icons/close2.svg'
import ThemeInterface from '@/theme-interface'
import { toast } from '@/modules/base/components/toast/toastConfig'
import { isMeinBuero } from '@/utils/build-env'
import { MenuItem, Select, SelectChangeEvent } from '@mui/material'
import RichTooltip from '@/modules/base/components/rich-tooltip/rich-tooltip'

const { emptyRequiredFieldErrorMessage: NOT_EMPTY_MESSAGE } = lang

const ICON_BEFORE = 'before'
const ICON_AFTER = 'after'

export const VARIANT_LIGHT = 'light'
export const VARIANT_DARK = 'dark'

interface TextInputProps {
  type?: string
  value?: string
  name: string
  autoComplete?: string
  // the virtual keyboard to show on touch devices, e.g. 'numeric' for a digit code
  inputMode?: React.HTMLAttributes<HTMLInputElement>['inputMode']
  classes?: {
    input?: CSSProperties
    [key: string]: CSSProperties
  }
  placeholder?: string
  hideErrorMessage?: boolean
  useFormik?: boolean
  enableCopy?: boolean
  isClearable?: boolean
  enableReveal?: boolean
  disabled?: boolean
  readOnly?: boolean
  required?: boolean
  maxLength?: number
  icon?: string
  iconPosition?: 'before' | 'after'
  variant?: 'light' | 'dark'
  onIconClick?: () => void
  onKeyUp?: (event: KeyboardEvent<HTMLInputElement>, name: string) => void
  onKeyDown?: (event: KeyboardEvent<HTMLInputElement>, name: string) => void
  onPaste?: (value: string, name: string, event?: ClipboardEvent<HTMLInputElement>) => void
  onChange?: (value: string, name: string) => void
  onBlur?: (value: string, name: string) => void
  // Fired when the clear icon is used. Lets a caller that only commits on blur still see the reset.
  onClear?: (name: string) => void
  onFocus?: () => void
  onEnter?: (event: KeyboardEvent<HTMLInputElement>) => void
  onClick?: (event: React.MouseEvent<HTMLInputElement>) => void
  // ReactNode, not string: the review marks a flagged field INSIDE its label (see
  // review-field-check.tsx). Widening only — every caller passing a plain string is
  // unaffected, and the render below already just interpolates the value.
  label?: React.ReactNode
  isNonEditable?: boolean
  customClass?: string
  className?: string | object
  errorMessage?: string
  showFillAnimation?: boolean
  tooltip?: string
  invalidCharacters?: RegExp[] | null
  selectOnFocus?: boolean
  autoFocus?: boolean
  readOnlyLabelEnabled?: boolean
  fillAnimationOptions?: {
    show: boolean
    color: string
  }
  labelTooltip?: {
    title: string
    description: string
    placement?: 'top' | 'bottom' | 'left' | 'right'
    showInfoIcon?: boolean
  }
  dataQsId: string
  enableAiFeatures?: boolean
  placeholders?: string[] | {
    label: string; value: string
  }[]
  disableMouseWheel?: boolean
  disableArrowKeys?: boolean
  inputId?: string
  customClassLabel?: string
  customClassError?: string
}

const validateFileName = (fileName, invalidCharacters) => {
  if (invalidCharacters.some(char => fileName.includes(char))) {
    toast.showToast({ type: 'error', message: lang.numerationSpecialCharsError(invalidCharacters.join(' ')) })
    return false
  }
}

const handleFileNameValidation = (value, invalidCharacters) => {
  if (!invalidCharacters) return value
  const hasInvalidCharacters = !validateFileName(value, invalidCharacters)
  if (!hasInvalidCharacters) return value
  return invalidCharacters.reduce((acc, char) => acc.replace(char, ''), value)
}

interface StyleProps {
  readOnly?: boolean
  error?: string
  inFocus?: boolean
  icon?: string
  readOnlyLabelEnabled?: boolean
  enableCopy?: boolean
  isClearable?: boolean
  enableReveal?: boolean
  iconPosition?: typeof ICON_BEFORE | typeof ICON_AFTER
  variant?: typeof VARIANT_LIGHT | typeof VARIANT_DARK
  classes?: { input?: CSSProperties }
  isNonEditable?: boolean
}

const useStyles = makeStyles((theme: ThemeInterface) => ({
  container: ({ readOnly, error, inFocus, icon }: StyleProps) => ({
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    position: 'relative',
    color: readOnly ? theme.disabledColor : theme.text,
    borderStyle: 'solid',
    borderWidth: icon ? 1 : 0,
    borderColor: inFocus ? (error ? theme.red : theme.activeInputBorder) : 'transparent',
    borderRadius: theme.borderRadius,
  }),
  label: (props) => ({
    display: 'block',
    color: props.readOnly && !props.readOnlyLabelEnabled ? theme.disabledColor : theme.text,
    fontSize: 13,
    marginBottom: 10,
  }),
  input: ({ error, enableCopy, isClearable, enableReveal, classes, icon, iconPosition, variant, readOnly, isNonEditable }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let style: any = {
      outline: 'none',
      border: `1px solid ${error ? theme.red : theme.borderColor}`,
      borderRadius: theme.borderRadius,
      color: theme.text,
      height: 45,
      paddingLeft: 15,
      paddingRight: (enableCopy || isClearable) ? (enableReveal ? 75 : 30) : 15,
      cursor: isNonEditable ? 'pointer' : 'text',
      width: '100%',
      '&:focus': {
        background: !readOnly && '#ffffff',
      },
      '&:disabled': {
        background: theme.backgroundColorDisabled,
      },
      ...classes.input,
      ...variants[variant](theme).input,
    }

    if (icon) {
      if (iconPosition === ICON_BEFORE) {
        style = {
          ...style,
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
          borderLeft: 0,
        }
      } else if (iconPosition === ICON_AFTER) {
        style = {
          ...style,
          borderTopRightRadius: 0,
          borderBottomRightRadius: 0,
          borderRight: 0,
        }
      }

      style = {
        ...style,
        '&:focus': {
          background: '#fff',
          '& + $iconWrapper': {
            background: '#fff',
          },
        },
      }
    } else {
      style = {
        ...style,
        '&:focus': {
          background: !readOnly && '#ffffff',
          borderColor: error ? theme.red : !readOnly && theme.activeInputBorder,
        },
      }
    }

    return style
  },
  error: {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: 12,
    color: theme.red,
    marginTop: 10,
  },
  inputWrapper: {
    position: 'relative',
    width: '100%',
    display: 'flex',
  },
  copyClipboard: {
    height: 20,
    width: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    right: 12.5,
    top: 12.5,
    cursor: 'pointer',
    transition: 'background-color .5s',
    fill: theme.darkerGrey,
  },
  clearableContainer: {
    height: 20,
    width: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    right: 0,
    top: '50%',
    transform: 'translate(-12px, -50%)',
    cursor: 'pointer',
    '&:hover': {
      background: theme.darkerGrey,
      fill: theme.white,

    },
  },
  reveal: (props) => ({
    position: 'absolute',
    right: props.enableCopy ? 48.5 : 12.5,
    top: 14,
    cursor: 'pointer',
    '& path': {
      fill: theme.darkerGrey,
    },
  }),
  iconWrapper: ({ error, iconPosition, variant }) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let style: any = {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: 45,
      ...variants[variant](theme).input,
    }

    if (iconPosition === ICON_BEFORE) {
      style = {
        ...style,
        borderTopLeftRadius: theme.borderRadius,
        borderBottomLeftRadius: theme.borderRadius,
      }
    } else if (iconPosition === ICON_AFTER) {
      style = {
        ...style,
        borderTopRightRadius: theme.borderRadius,
        borderBottomRightRadius: theme.borderRadius,
        borderRight: `1px solid  ${error ? theme.red : theme.borderColor}`,
        borderTop: `1px solid  ${error ? theme.red : theme.borderColor}`,
        borderBottom: `1px solid  ${error ? theme.red : theme.borderColor}`,
      }
    }
    return style
  },
  replacementSelect: {
    width: 200,
    marginBottom: 3,
    height: 32,
    '& .MuiSelect-select': {
      marginTop: 3,
    },
  },
  optionLabel: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    gap: 10,
    '& > $label': {
      marginBottom: 0,
    },
    justifyContent: 'space-between',
  },
  tooltipWithInfoIcon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 10,
    gap: 10,
    '& > $label': {
      marginBottom: 0,
    },
  },
  tooltip: {
    fontSize: theme.fontSizes.smaller,
    fontWeight: theme.fontWeights.regular,
  },
}))

const isMB = isMeinBuero()

const normaliseReplacements = (
  list?: string[] | {label:string; value:string}[],
): {label:string; value:string}[] =>
  (list ?? []).map(item =>
    typeof item === 'string' ? { label: item, value: item } : item,
  )

const TextInput = React.forwardRef<HTMLInputElement, TextInputProps>(function TextInput(props, forwardRef) {
  const {
    name,
    type = 'text',
    label,
    customClass,
    className,
    readOnly = false,
    placeholder,
    value: propValue = '',
    autoFocus,
    autoComplete = '',
    inputMode,
    maxLength,
    errorMessage,
    hideErrorMessage = false,
    selectOnFocus,
    required,
    onKeyUp,
    onKeyDown,
    onPaste,
    onChange,
    onBlur,
    onClear,
    onFocus,
    onEnter,
    onClick,
    fillAnimationOptions = {},
    useFormik,
    enableCopy = false,
    isClearable,
    enableReveal = false,
    disabled = false,
    onIconClick = () => {},
    icon = null,
    iconPosition = null,
    variant = VARIANT_DARK,
    tooltip,
    invalidCharacters = null,
    isNonEditable = false,
    dataQsId,
    enableAiFeatures = true,
    placeholders = [],
    labelTooltip,
    disableMouseWheel = false,
    disableArrowKeys = false,
    inputId = `input-${shortId()}`,
    customClassLabel,
    customClassError,
  } = props

  const { isCopied, handleCopy } = useCopyToClipboard()
  const [internalType, setInternalType] = useState(type)
  const [error, setError] = useState(errorMessage)
  const [inFocus, setInFocus] = useState(autoFocus)
  const [replacementValue, setReplacementValue] = useState('')
  const formik = useFormik === undefined || useFormik ? useFormikContext() : null
  const field = formik?.getFieldProps(name)
  const fieldMeta = formik?.getFieldMeta(name)

  const classes = useStyles({
    ...props,
    classes: props.classes || {},
    error: error || (fieldMeta?.touched && fieldMeta?.error),
    inFocus,
    variant,
  })
  const [value, setValue] = useState(propValue)

  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const { renderAiComponents } = useAiTextTransform({
    containerRef,
    enabled: (!isMB && enableAiFeatures) && !readOnly && !disabled && !isNonEditable,
    inputRef,
    onApplyResult: useCallback((result: string) => {
      const input = inputRef.current || (typeof forwardRef === 'object' && forwardRef?.current)
      if (!input) return

      const start = input.selectionStart || 0
      const end = input.selectionEnd || 0
      const currentValue = input.value

      const newValue = currentValue.substring(0, start) + result + currentValue.substring(end)
      const event = {
        target: {
          value: newValue,
          name,
        },
      } as React.ChangeEvent<HTMLInputElement>

      setError(errorMessage)
      if (required && !newValue.trim()) {
        setError(NOT_EMPTY_MESSAGE)
      }

      if (!field) {
        setValue(newValue)
      }
      if (field && field.onChange) {
        field.onChange(event)
      }
      onChange?.(newValue, name)

      setTimeout(() => {
        const newCursorPos = start + result.length
        input.setSelectionRange(newCursorPos, newCursorPos)
        input.focus()
      }, 0)
    }, [name, forwardRef, errorMessage, required, field, onChange]),
  })

  useEffect(() => {
    setValue(propValue)
  }, [propValue])

  useEffect(() => {
    setError(errorMessage)
  }, [propValue, errorMessage])

  let errorEl = null
  if (field) {
    errorEl = <ErrorMessage name={name} className={clsx(classes.error, customClassError)} component="div" />
  } else {
    errorEl = error ? <div className={clsx(classes.error, customClassError)}>{error}</div> : null
  }

  const handleKeyUp = (event: KeyboardEvent<HTMLInputElement>) => {
    onKeyUp?.(event, name)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (disableArrowKeys && type === 'number' && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
      event.preventDefault()
    }
    if (event.key === 'Enter') {
      onEnter?.(event)
    }
    onKeyDown?.(event, name)
  }

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const value = event.clipboardData.getData('text/plain')
    onPaste?.(value, name, event)
  }

  const handleInputFocus = (event: FocusEvent<HTMLInputElement>) => {
    setInFocus(true)
    if (!selectOnFocus) return
    onFocus?.()
    event.target.select()
  }

  const handleInputBlur = (event: FocusEvent<HTMLInputElement>) => {
    setInFocus(false)
    const { value: val } = event.target
    let value = val
    if (readOnly || isNonEditable) {
      return
    }
    value = handleFileNameValidation(value, invalidCharacters)
    if (required && !value) {
      setError(NOT_EMPTY_MESSAGE)
    }
    if (!field) {
      setValue(value)
    }
    if (field && field.onBlur) {
      field.onBlur(event)
    }
    onBlur?.(value, name)
  }

  const handleReplacementSelect = (event: SelectChangeEvent<string>) => {
    const picked = event.target.value ?? ''
    const el = inputRef.current
    if (!el) return

    const start = el.selectionStart ?? 0
    const end = el.selectionEnd ?? 0
    const current = field ? (field.value ?? '') : value

    const newValue = current.slice(0, start) + picked + current.slice(end)

    /* reuse your existing plumbing so validation / Formik stay happy */
    handleInputChange({ target: { value: newValue } } as { target: { value: string } })
    if (formik) {
      formik.getFieldHelpers(field.name).setValue(newValue)
    }

    /* put the caret right after the inserted text */
    requestAnimationFrame(() => {
      const pos = start + picked.length
      el.setSelectionRange(pos, pos)
      el.focus()
    })

    // onReplacementSelect?.(picked, name)

    /* reset the <select> back to its placeholder */
    setReplacementValue('')
  }

  const handleInputChange = (event) => {
    const { value: val } = event.target
    let value = val
    if (readOnly || isNonEditable) {
      return
    }
    value = handleFileNameValidation(value, invalidCharacters)
    setError(errorMessage)
    if (required && !value.trim()) {
      setError(NOT_EMPTY_MESSAGE)
    }
    if (!field) {
      setValue(value)
    }
    if (field && field.onChange) {
      field.onChange(event)
    }
    onChange?.(value, name)
  }

  const handleClear = () => {
    handleInputChange({ target: { value: '' } })
    onClear?.(name)
  }

  const handleClick = (event: React.MouseEvent<HTMLInputElement>) => {
    onClick?.(event)
  }

  const handleWheel = (event: React.WheelEvent<HTMLInputElement>) => {
    if (type === 'number') {
      event.preventDefault()
      if (disableMouseWheel) {
        event.currentTarget.blur()
      }
    }
  }

  let labelEl = label ? <label htmlFor={inputId} className={clsx(classes.label, customClassLabel)}>{label}</label> : null
  if (labelEl) {
    const replacements = normaliseReplacements(placeholders)
    if (replacements.length) {
      labelEl = (
        <div className={classes.optionLabel}>
          {labelEl}
          <Select
            labelId="replacement-select-label"
            name="replacementSelect"
            onChange={handleReplacementSelect}
            value={replacementValue}
            displayEmpty
            disabled={disabled || readOnly}
            className={classes.replacementSelect}
          >
            <MenuItem key="placeholder" value="">
              {lang.placeHolder}
            </MenuItem>
            {replacements.map(r => (
              <MenuItem key={r.value} value={`{{${r.value}}}`}>
                {r.label}
              </MenuItem>
            ))}
          </Select>
        </div>
      )
    }
  }
  if (labelTooltip && labelEl) {
    const tooltipEl = (
      <>
        {labelTooltip.showInfoIcon && labelEl}
        <RichTooltip
          heading={labelTooltip?.title}
          body={labelTooltip?.description}
          width='500px'
          styles={{ tooltip: classes.tooltip }}
          placement={labelTooltip.placement || 'right'}>
          {labelTooltip.showInfoIcon ? <SVGIcon width={14} height={14} svg={IconInfo} /> : labelEl}
        </RichTooltip>
      </>
    )
    labelEl = labelTooltip.showInfoIcon ? <div className={classes.tooltipWithInfoIcon}>{tooltipEl}</div> : tooltipEl
  }

  return (
    <div className={clsx(classes.container, customClass, className)} data-qs-id={dataQsId} ref={containerRef}>
      {labelEl}
      <FillTransition {...fillAnimationOptions}>
        <div className={classes.inputWrapper} data-tooltip={tooltip}>
          {iconPosition === ICON_BEFORE && (
            <div className={classes.iconWrapper}>
              <SVGIcon
                width="15px"
                height="15px"
                svg={icon}
                onClick={onIconClick}
              />
            </div>
          )}
          <input
            type={internalType}
            id={inputId}
            name={name}
            className={classes.input}
            ref={(el) => {
              inputRef.current = el
              if (forwardRef) {
                if (typeof forwardRef === 'function') {
                  forwardRef(el)
                } else {
                  forwardRef.current = el
                }
              }
            }}
            maxLength={maxLength}
            placeholder={placeholder}
            value={field ? (field.value ?? '') : value}
            {...(autoComplete ? { autoComplete } : {})}
            inputMode={inputMode}
            autoFocus={autoFocus}
            disabled={disabled}
            readOnly={readOnly || isNonEditable}
            onChange={handleInputChange}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            onKeyUp={handleKeyUp}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            onClick={handleClick}
            onWheel={handleWheel}
          />
          {enableCopy && (
            <SVGIcon
              className={classes.copyClipboard}
              width="10px"
              height="10px"
              svg={isCopied ? CheckIcon : copyClipboardIcon}
              onClick={() => handleCopy(inputId)}
            />
          )}
          {!readOnly && isClearable && !(value == null || value === '') && (
            <div className={classes.clearableContainer}>
              <SVGIcon
                className={'clearable'}
                width="10px"
                height="10px"
                svg={CloseIcon}
                onClick={handleClear}
              />
            </div>
          )}
          {type === 'password' && enableReveal && (
            <SVGIcon
              className={classes.reveal}
              width="18px"
              height="12px"
              svg={revealIcon}
              onClick={() => setInternalType(internalType === 'password' ? 'text' : type)}
            />
          )}
          {iconPosition === ICON_AFTER && (
            <div className={classes.iconWrapper}>
              <SVGIcon
                width="15px"
                height="15px"
                svg={icon}
                onClick={onIconClick}
              />
            </div>
          )}
        </div>
      </FillTransition>
      {!hideErrorMessage && errorEl}
      {renderAiComponents()}
    </div>
  )
})

const variants = {
  [VARIANT_LIGHT]: (theme: ThemeInterface) => ({
    input: {
      background: theme.backgroundColorLight,
    },
  }),
  [VARIANT_DARK]: (theme: ThemeInterface) => ({
    input: {
      background: theme.backgroundColor,
    },
  }),
}

export default TextInput
